import { checkActivityFunctions } from 'single-spa'
import { Freer } from 'qiankun'

const rawHtmlAppendChild = HTMLHeadElement.prototype.appendChild

const LINK_TAG_NAME = 'LINK'
const STYLE_TAG_NAME = 'STYLE'

const exceptSubApp = ['SUB_APP__CRM_V2', 'SUB_APP__TMS', 'SUB_APP__TALK', 'SUB_APP__NEW_DISCOVERY']

// seajs 的实现
function styleOnload(stylesheet: HTMLLinkElement, callback: Function) {
  let timer: number

  function poll(stylesheet: HTMLLinkElement, callback: Function) {
    let isLoaded = false
    if (/webkit/i.test(navigator.userAgent)) {
      //webkit
      if (stylesheet['sheet']) {
        isLoaded = true
      }
    }
    // for Firefox
    else if (stylesheet['sheet']) {
      try {
        if ((stylesheet['sheet'] as CSSStyleSheet).cssRules) {
          isLoaded = true
        }
      } catch (ex) {
        // NS_ERROR_DOM_SECURITY_ERR
        if (ex.code === 1000 || ex.code === 18) {
          isLoaded = true
        }
      }
    }
    if (isLoaded) {
      if (timer) {
        clearTimeout(timer)
      }

      // give time to render.
      setTimeout(function () {
        callback()
      }, 1)
    } else {
      timer = setTimeout(function () {
        poll(stylesheet, callback)
      }, 1)
    }
  }

  poll(stylesheet, callback)
}

export default function hijack(
  appName: string,
  blockList: (string | RegExp | ((el: HTMLLinkElement) => boolean))[] = [],
): Freer {
  let dynamicStyleSheets: [HTMLLinkElement | HTMLStyleElement, Comment][] = []

  HTMLHeadElement.prototype.appendChild = function appendChild<T extends Node>(this: any, newChild: T) {
    const element = newChild as any
    if (element.tagName) {
      switch (element.tagName) {
        case LINK_TAG_NAME:
        case STYLE_TAG_NAME: {
          const stylesheetElement = (newChild as any) as HTMLLinkElement | HTMLStyleElement

          // check if the currently specified application is active
          // While we switch page from qiankun app to a normal react routing page, the normal one may load stylesheet dynamically while page rendering,
          // but the url change listener must to wait until the current call stack is flushed.
          // This scenario may cause we record the stylesheet from react routing page dynamic injection,
          // and remove them after the url change triggered and qiankun app is unmouting
          // see https://github.com/ReactTraining/history/blob/master/modules/createHashHistory.js#L222-L230
          const activated = checkActivityFunctions(window.location).some((name) => name === appName)
          // only hijack dynamic style injection when app activated
          if (activated) {
            dynamicStyleSheets.push([
              stylesheetElement,
              document.createComment(
                stylesheetElement instanceof HTMLLinkElement
                  ? `entry link of ${appName}: ${stylesheetElement.href}`
                  : `entry style of ${appName}`,
              ),
            ])
          }

          break
        }
        default:
          break
      }
    }

    return rawHtmlAppendChild.call(this, element) as T
  }

  return function free() {
    HTMLHeadElement.prototype.appendChild = rawHtmlAppendChild
    dynamicStyleSheets = dynamicStyleSheets.filter(
      ([stylesheet]) =>
        document.head.contains(stylesheet) &&
        (stylesheet instanceof HTMLStyleElement ||
          !blockList.some((item) =>
            typeof item === 'string'
              ? stylesheet.href === item
              : typeof item === 'function'
              ? item(stylesheet)
              : item.test(stylesheet.href),
          )),
    )
    dynamicStyleSheets.forEach(([stylesheet, comment], index, array) => {
      // the dynamic injected stylesheet may had been removed by itself while unmounting
      try {
        document.head.replaceChild(comment, stylesheet)
      } catch (e) {}
    })

    if (exceptSubApp.includes(appName)) {
      return function rebuild() {
        return new Promise((resolve) =>
          resolve(
            dynamicStyleSheets.map(([stylesheet, comment]) => {
              document.head.replaceChild(stylesheet, comment)
            }),
          ),
        )
      }
    }
    return function rebuild() {
      return Promise.all(
        dynamicStyleSheets.map(([stylesheet, comment]) => {
          return new Promise((resolve) => {
            document.head.replaceChild(stylesheet, comment)
            return stylesheet instanceof HTMLStyleElement ? setTimeout(resolve, 1) : styleOnload(stylesheet, resolve)
          })
        }),
      )
    }
  }
}
